import React from 'react';
import { useTranslation } from 'react-i18next';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Typography } from '@mui/material';
import { DateTime } from 'luxon';

import { BOOKING_DEADLINE_HOURS, TIME_ZONE } from '@common/constants/academyReworkings';
import { parseDate } from '@common/helpers/academyReworkings';

import { BookingFlow } from '../../hooks/useBookingFlow';
import { nextButtonSx } from '../../styled';
import ErrorNotice from '../ErrorNotice';

export default function Days({ flow }: { flow: BookingFlow }) {
	const { t } = useTranslation();
	const days = Array.from({ length: 7 }, (_, index) => flow.week.plus({ days: index }));
	const hasAvailableDays = days.some((day) =>
		flow.availableWindows.some((window) => parseDate(window.start).hasSame(day, 'day')),
	);
	const canContinue = flow.selectedDays.some((day) =>
		flow.availableWindows.some((window) => parseDate(window.start).toISODate() === day),
	);

	return (
		<Box display="flex" flexDirection="column" gap="20px">
			<Box>
				<Typography variant="h3">{t('academyReworkings.chooseDays')}</Typography>
				<Typography color="text.secondary" sx={{ mt: 1 }}>
					{t('academyReworkings.daysHint')}
				</Typography>
			</Box>
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: '44px minmax(0, 1fr) 44px',
					alignItems: 'center',
					gap: '8px',
				}}
			>
				<IconButton
					aria-label={t('academyReworkings.previousWeek')}
					onClick={() => flow.changeWeek(flow.week.minus({ weeks: 1 }))}
					sx={{
						width: '44px',
						height: '44px',
						border: '1px solid',
						borderColor: 'divider',
						borderRadius: '12px',
						bgcolor: 'background.paper',
					}}
				>
					<ChevronLeftIcon sx={{ fontSize: '28px' }} />
				</IconButton>
				<Box sx={{ minWidth: 0, textAlign: 'center' }}>
					<Typography
						fontWeight={700}
						sx={{ whiteSpace: 'nowrap', fontSize: '18px', lineHeight: 1.3 }}
					>
						{flow.week.toFormat('dd.MM')} – {flow.week.endOf('week').toFormat('dd.MM.yyyy')}
					</Typography>
					<Button
						size="small"
						onClick={() => flow.changeWeek(DateTime.now().setZone(TIME_ZONE))}
						sx={{
							p: '0 6px',
							mt: '2px',
							height: '24px',
							minHeight: '24px',
							lineHeight: 1.2,
							fontSize: '12px',
							textTransform: 'none',
						}}
					>
						{t('academyReworkings.currentWeek')}
					</Button>
				</Box>
				<IconButton
					aria-label={t('academyReworkings.nextWeek')}
					onClick={() => flow.changeWeek(flow.week.plus({ weeks: 1 }))}
					sx={{
						width: '44px',
						height: '44px',
						border: '1px solid',
						borderColor: 'divider',
						borderRadius: '12px',
						bgcolor: 'background.paper',
					}}
				>
					<ChevronRightIcon sx={{ fontSize: '28px' }} />
				</IconButton>
			</Box>
			{flow.slotsError && <ErrorNotice message={flow.slotsError} onRetry={flow.retrySlots} />}
			{flow.slotsLoading ? (
				<Box display="flex" justifyContent="center" py={4}>
					<CircularProgress />
				</Box>
			) : (
				<Box
					sx={(theme) => ({
						display: 'grid',
						minWidth: 0,
						gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
						[theme.breakpoints.up('sm')]: { gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' },
						[theme.breakpoints.up('lg')]: { gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' },
						gap: '10px',
					})}
				>
					{days.map((day) => {
						const date = day.toISODate();
						const selected = flow.selectedDays.includes(date);
						const count = flow.availableWindows.filter((window) =>
							parseDate(window.start).hasSame(day, 'day'),
						).length;
						return (
							<Button
								key={date}
								variant={selected ? 'contained' : 'outlined'}
								aria-pressed={selected}
								aria-label={t('academyReworkings.selectDay', { date: day.toFormat('dd.MM.yyyy') })}
								disabled={!!flow.slotsError || (!count && !selected)}
								onClick={() => flow.toggleDay(date)}
								sx={{
									position: 'relative',
									p: '12px 10px',
									minHeight: '104px',
									borderRadius: '14px',
									display: 'flex',
									flexDirection: 'column',
									gap: '2px',
									textTransform: 'none',
									...(!selected &&
										count > 0 && {
											bgcolor: 'background.paper',
											'&:hover': { bgcolor: 'background.paper', borderColor: 'primary.main' },
										}),
								}}
							>
								{selected && (
									<CheckCircleIcon
										sx={{ position: 'absolute', top: 6, right: 6, fontSize: '16px' }}
									/>
								)}
								<Typography component="span" variant="body2" sx={{ textTransform: 'capitalize' }}>
									{day.toFormat('cccc')}
								</Typography>
								<Typography
									component="span"
									sx={{ fontSize: '24px', fontWeight: 700, lineHeight: 1.25 }}
								>
									{day.toFormat('dd.MM')}
								</Typography>
								<Typography component="span" variant="caption">
									{t(count ? 'academyReworkings.availableTimes' : 'academyReworkings.noTimes', {
										count,
									})}
								</Typography>
							</Button>
						);
					})}
				</Box>
			)}
			{!flow.slotsLoading && !flow.slotsError && !hasAvailableDays && (
				<Alert severity="info">{t('academyReworkings.noSlots')}</Alert>
			)}
			{flow.selectedDays.length > 0 && (
				<Box display="flex" gap="8px" flexWrap="wrap">
					{flow.selectedDays.map((day) => (
						<Chip
							key={day}
							color="primary"
							variant="outlined"
							label={DateTime.fromISO(day).toFormat('ccc, dd.MM')}
							onDelete={() => flow.toggleDay(day)}
						/>
					))}
				</Box>
			)}
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				flexWrap="wrap"
				gap="16px"
			>
				<Typography color="text.secondary">
					{t('academyReworkings.selectedDays', { count: flow.selectedDays.length })}
				</Typography>
				<Button
					variant="contained"
					sx={nextButtonSx}
					disabled={!canContinue || flow.slotsLoading || !!flow.slotsError}
					onClick={() => flow.setStep('times')}
				>
					{t('academyReworkings.nextTime')}
				</Button>
			</Box>
			<Typography variant="body2" color="text.secondary">
				{t('academyReworkings.timeZone')}{' '}
				{t('academyReworkings.bookingDeadline', { hours: BOOKING_DEADLINE_HOURS })}
			</Typography>
		</Box>
	);
}
